export const computerSystemsArchitectureCourse = {
  id: 'computer-systems-architecture',
  title: 'Computer Systems and Architecture',
  description: 'Comprehensive study of computer organization, processor architecture, memory systems, and system-level programming with hands-on assembly language and system design experience.',
  credits: 3,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Coding',
  learningObjectives: [
    'Understand fundamental computer organization and architecture principles',
    'Master assembly language programming and low-level system operations',
    'Analyze processor design, instruction sets, and execution cycles',
    'Design and implement memory hierarchy systems and caching strategies',
    'Evaluate system performance and optimization techniques',
    'Apply knowledge to embedded systems and hardware-software interfaces'
  ],
  prerequisites: ['Introduction to Programming', 'Introduction to Computer Science'],
  skills: [
    'Computer Architecture',
    'Assembly Language Programming',
    'System-Level Programming',
    'Memory Management',
    'Processor Design',
    'Performance Analysis',
    'Embedded Systems',
    'Hardware-Software Interface',
    'Digital Logic',
    'Computer Organization',
    'System Optimization',
    'Low-Level Debugging'
  ],
  marketDemand: 'High',
  averageSalary: '$80,000 - $150,000',
  careerPaths: [
    'Systems Programmer',
    'Embedded Systems Engineer',
    'Hardware Engineer',
    'Computer Architect',
    'Firmware Developer',
    'Performance Engineer',
    'Systems Analyst',
    'Device Driver Developer',
    'Real-Time Systems Engineer',
    'Computer Security Specialist',
    'VLSI Design Engineer',
    'Microprocessor Designer'
  ],
  industryApplications: [
    'Embedded Systems Development',
    'Operating System Design',
    'Compiler Development',
    'Hardware Design',
    'IoT Device Programming',
    'Gaming Console Development',
    'Automotive Systems',
    'Aerospace Engineering',
    'Mobile Device Architecture',
    'High-Performance Computing',
    'Cybersecurity Systems',
    'Robotics Control Systems'
  ],
  modules: [
    {
      id: 'computer-organization-basics',
      title: 'Computer Organization Fundamentals',
      duration: '3 weeks',
      learningObjectives: [
        'Understand basic computer components and their interactions',
        'Learn number systems and data representation',
        'Explore digital logic and Boolean algebra',
        'Analyze computer system architecture models'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Computer Components and Von Neumann Architecture',
          content: `# Computer Organization Fundamentals

## Von Neumann Architecture

The foundation of modern computers:

### Core Components
- **CPU (Central Processing Unit)**: Executes instructions
- **Memory**: Stores programs and data
- **Input/Output Devices**: Interface with external world
- **Bus System**: Connects all components

### Key Principles
1. **Stored Program Concept**: Instructions and data stored in same memory
2. **Sequential Execution**: Instructions executed one after another
3. **Binary Representation**: All data represented in binary

## Data Representation

### Number Systems
- **Binary (Base 2)**: 0, 1
- **Decimal (Base 10)**: 0-9
- **Hexadecimal (Base 16)**: 0-9, A-F

### Integer Representation
- **Unsigned**: Direct binary representation
- **Signed**: Two's complement for negative numbers
- **Floating Point**: IEEE 754 standard

### Character Encoding
- **ASCII**: 7-bit character encoding
- **Unicode**: Universal character encoding
- **UTF-8**: Variable-length Unicode encoding

## Digital Logic Basics

### Boolean Algebra
- AND, OR, NOT operations
- De Morgan's Laws
- Logic simplification

### Basic Gates
- AND, OR, NOT, NAND, NOR, XOR
- Truth tables and logic equations
- Gate combinations for complex functions

## Programming Implementation

\`\`\`javascript
class NumberSystemConverter {
  // Convert decimal to binary
  static decimalToBinary(decimal) {
    return (decimal >>> 0).toString(2);
  }
  
  // Convert binary to decimal
  static binaryToDecimal(binary) {
    return parseInt(binary, 2);
  }
  
  // Two's complement representation
  static twosComplement(value, bits = 8) {
    if (value >= 0) {
      return value.toString(2).padStart(bits, '0');
    } else {
      return ((1 << bits) + value).toString(2);
    }
  }
  
  // IEEE 754 single precision (simplified)
  static floatToBinary(float) {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);
    view.setFloat32(0, float);
    const bits = view.getUint32(0);
    return bits.toString(2).padStart(32, '0');
  }
}

// Test implementations
console.log("Binary of 42:", NumberSystemConverter.decimalToBinary(42));
console.log("Two's complement of -5:", NumberSystemConverter.twosComplement(-5, 8));
\`\`\``
        },
        {
          type: 'interactive_exercise',
          title: 'Number System and Logic Gate Simulator',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a computer architecture simulator that handles:

1. Number system conversions (binary, decimal, hex)
2. Two's complement arithmetic operations
3. Basic logic gate operations
4. Simple CPU instruction simulation

Implement:
- NumberConverter class with conversion methods
- LogicGate class that simulates AND, OR, NOT, XOR gates
- SimpleCPU class that can execute basic instructions (ADD, SUB, LOAD, STORE)

Test with: Convert 42 to binary, perform two's complement of -10, simulate AND gate with inputs true/false, execute ADD instruction`,
            initialCode: `class NumberConverter {
  static binaryToDecimal(binary) {
    // Convert binary string to decimal
  }
  
  static decimalToBinary(decimal) {
    // Convert decimal to binary string
  }
  
  static twosComplement(value, bits = 8) {
    // Calculate two's complement representation
  }
}

class LogicGate {
  static and(a, b) {
    // Implement AND gate
  }
  
  static or(a, b) {
    // Implement OR gate
  }
  
  static not(a) {
    // Implement NOT gate
  }
  
  static xor(a, b) {
    // Implement XOR gate
  }
}

class SimpleCPU {
  constructor() {
    this.registers = { A: 0, B: 0, C: 0 };
    this.memory = new Array(256).fill(0);
  }
  
  load(register, value) {
    // Load value into register
  }
  
  add(regA, regB, regC) {
    // Add regA + regB, store in regC
  }
  
  store(register, address) {
    // Store register value to memory address
  }
}

// Test implementations
const cpu = new SimpleCPU();
console.log("42 in binary:", NumberConverter.decimalToBinary(42));
console.log("Two's complement of -10:", NumberConverter.twosComplement(-10));
console.log("AND gate (true, false):", LogicGate.and(true, false));

cpu.load('A', 5);
cpu.load('B', 3);
cpu.add('A', 'B', 'C');
console.log("CPU Register C after ADD:", cpu.registers.C);`,
            expectedOutput: `42 in binary: 101010
Two's complement of -10: 11110110
AND gate (true, false): false
CPU Register C after ADD: 8`,
            hints: [
              'Use parseInt(binary, 2) for binary to decimal conversion',
              'For two\'s complement: flip bits and add 1, or use bit manipulation',
              'Logic gates: AND uses &&, OR uses ||, NOT uses !, XOR uses !==',
              'CPU registers are simple variables storing numbers',
              'ADD operation: registers[regC] = registers[regA] + registers[regB]'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Architecture Fundamentals Quiz',
          title: 'Computer Organization and Number Systems',
          points: 75,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'processor-architecture',
      title: 'Processor Architecture and Instruction Sets',
      duration: '4 weeks',
      learningObjectives: [
        'Understand CPU components and their functions',
        'Learn instruction set architecture principles',
        'Master assembly language programming',
        'Analyze instruction execution cycles'
      ],
      content: [
        {
          type: 'lesson',
          title: 'CPU Architecture and Instruction Execution',
          content: `# Processor Architecture

## CPU Components

### Control Unit (CU)
- Fetches instructions from memory
- Decodes instructions
- Controls execution sequence
- Manages data flow

### Arithmetic Logic Unit (ALU)
- Performs arithmetic operations
- Executes logical operations
- Handles comparisons
- Sets status flags

### Registers
- **Program Counter (PC)**: Address of next instruction
- **Instruction Register (IR)**: Current instruction
- **Accumulator**: Stores computation results
- **General Purpose**: Data storage and manipulation

## Instruction Set Architecture (ISA)

### RISC vs CISC
**RISC (Reduced Instruction Set Computing)**:
- Simple, uniform instructions
- Fixed-length instructions
- Load/store architecture
- Examples: ARM, MIPS

**CISC (Complex Instruction Set Computing)**:
- Complex, variable-length instructions
- Memory-to-memory operations
- Fewer instructions for same task
- Examples: x86, 68000

### Instruction Types
1. **Data Movement**: LOAD, STORE, MOVE
2. **Arithmetic**: ADD, SUB, MUL, DIV
3. **Logical**: AND, OR, XOR, NOT
4. **Control Flow**: JMP, CALL, RET, conditional branches
5. **Comparison**: CMP, TEST

## Assembly Language Programming

### Basic MIPS Assembly
\`\`\`assembly
.data
    msg: .asciiz "Hello World"
    num1: .word 10
    num2: .word 20

.text
main:
    # Load values
    lw $t0, num1
    lw $t1, num2
    
    # Add numbers
    add $t2, $t0, $t1
    
    # Store result
    sw $t2, result
    
    # Exit program
    li $v0, 10
    syscall
\`\`\`

### JavaScript Assembly Simulator
\`\`\`javascript
class MIPSSimulator {
  constructor() {
    this.registers = {
      $zero: 0, $at: 0, $v0: 0, $v1: 0,
      $a0: 0, $a1: 0, $a2: 0, $a3: 0,
      $t0: 0, $t1: 0, $t2: 0, $t3: 0,
      $t4: 0, $t5: 0, $t6: 0, $t7: 0,
      $s0: 0, $s1: 0, $s2: 0, $s3: 0,
      $s4: 0, $s5: 0, $s6: 0, $s7: 0,
      $t8: 0, $t9: 0, $k0: 0, $k1: 0,
      $gp: 0, $sp: 1000, $fp: 0, $ra: 0
    };
    this.memory = new Array(1024).fill(0);
    this.pc = 0;
    this.instructions = [];
  }
  
  // Load immediate value
  li(register, value) {
    this.registers[register] = value;
  }
  
  // Add registers
  add(rd, rs, rt) {
    this.registers[rd] = this.registers[rs] + this.registers[rt];
  }
  
  // Load word from memory
  lw(rt, offset, base) {
    const address = this.registers[base] + offset;
    this.registers[rt] = this.memory[address];
  }
  
  // Store word to memory
  sw(rt, offset, base) {
    const address = this.registers[base] + offset;
    this.memory[address] = this.registers[rt];
  }
  
  // Execute instruction
  execute(instruction) {
    const parts = instruction.trim().split(/[\s,()]+/);
    const op = parts[0];
    
    switch(op) {
      case 'li':
        this.li(parts[1], parseInt(parts[2]));
        break;
      case 'add':
        this.add(parts[1], parts[2], parts[3]);
        break;
      case 'lw':
        this.lw(parts[1], parseInt(parts[2]), parts[3]);
        break;
      case 'sw':
        this.sw(parts[1], parseInt(parts[2]), parts[3]);
        break;
    }
    this.pc++;
  }
}
\`\`\``
        },
        {
          type: 'interactive_exercise',
          title: 'Assembly Language Programming',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a MIPS assembly simulator that can execute basic instructions:

1. Implement registers and memory
2. Support these instructions: LI, ADD, SUB, LW, SW, BEQ
3. Create a program that calculates factorial of 5
4. Track register states during execution

Your simulator should:
- Parse assembly instructions
- Execute them step by step
- Maintain register and memory state
- Provide debugging output

Write a factorial program and simulate its execution.`,
            initialCode: `class AssemblySimulator {
  constructor() {
    this.registers = {
      '$t0': 0, '$t1': 0, '$t2': 0, '$t3': 0,
      '$s0': 0, '$s1': 0, '$a0': 0, '$v0': 0
    };
    this.memory = new Array(100).fill(0);
    this.pc = 0;
    this.labels = {};
  }
  
  // Parse and execute assembly program
  execute(program) {
    const lines = program.split('\\n').filter(line => line.trim());
    const instructions = this.parseProgram(lines);
    
    while (this.pc < instructions.length) {
      this.executeInstruction(instructions[this.pc]);
      this.pc++;
    }
    
    return this.registers;
  }
  
  parseProgram(lines) {
    // Parse assembly lines into executable instructions
    // Handle labels for jumps/branches
  }
  
  executeInstruction(instruction) {
    // Execute individual assembly instruction
    // Support: LI, ADD, SUB, LW, SW, BEQ, J
  }
  
  // Factorial calculation program
  getFactorialProgram() {
    return \`
      li $t0, 5        # Load n = 5
      li $t1, 1        # Load factorial = 1
      li $t2, 1        # Load counter = 1
    loop:
      beq $t2, $t0, done  # if counter == n, exit
      add $t2, $t2, 1     # counter++
      mul $t1, $t1, $t2   # factorial *= counter
      j loop              # jump to loop
    done:
      add $v0, $t1, $zero # result in $v0
    \`;
  }
}

// Test implementation
const simulator = new AssemblySimulator();
const program = simulator.getFactorialProgram();
const result = simulator.execute(program);

console.log("Final registers:", result);
console.log("Factorial of 5:", result['$v0']);`,
            expectedOutput: `Final registers: { '$t0': 5, '$t1': 120, '$t2': 5, '$t3': 0, '$s0': 0, '$s1': 0, '$a0': 0, '$v0': 120 }
Factorial of 5: 120`,
            hints: [
              'Parse instructions by splitting on whitespace and commas',
              'Use object properties to map instruction names to functions',
              'Track labels during parsing for jump/branch targets',
              'BEQ: if reg1 == reg2, jump to label',
              'MUL: multiply two registers, store in destination',
              'Handle program counter updates for jumps and branches'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Assembly Programming Project',
          title: 'MIPS Assembly Language Implementation',
          points: 125,
          dueWeek: 7
        }
      ]
    },
    {
      id: 'memory-systems',
      title: 'Memory Hierarchy and Cache Systems',
      duration: '4 weeks',
      learningObjectives: [
        'Understand memory hierarchy principles',
        'Design and analyze cache systems',
        'Implement virtual memory concepts',
        'Optimize memory access patterns'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Memory Hierarchy and Cache Design',
          content: `# Memory Systems

## Memory Hierarchy

### Levels (Fast to Slow)
1. **Registers**: Fastest, smallest capacity
2. **L1 Cache**: Very fast, small (32KB-64KB)
3. **L2 Cache**: Fast, medium (256KB-1MB)
4. **L3 Cache**: Moderate, larger (8MB-32MB)
5. **Main Memory (RAM)**: Slow, large (4GB-64GB)
6. **Secondary Storage**: Slowest, largest (TB)

### Locality Principles
- **Temporal Locality**: Recently accessed data likely to be accessed again
- **Spatial Locality**: Nearby data likely to be accessed soon

## Cache Design

### Cache Organization
- **Direct Mapped**: Each memory block maps to exactly one cache line
- **Set Associative**: Memory blocks can map to multiple cache lines
- **Fully Associative**: Any memory block can go anywhere in cache

### Cache Performance
- **Hit Rate**: Percentage of successful cache accesses
- **Miss Rate**: Percentage of failed cache accesses
- **Average Access Time**: Hit time + Miss rate × Miss penalty

### Cache Replacement Policies
- **LRU (Least Recently Used)**: Replace least recently accessed
- **FIFO (First In, First Out)**: Replace oldest entry
- **Random**: Replace random entry

## JavaScript Cache Simulator

\`\`\`javascript
class CacheSimulator {
  constructor(size, blockSize, associativity = 1) {
    this.size = size;
    this.blockSize = blockSize;
    this.associativity = associativity;
    this.numSets = size / (blockSize * associativity);
    
    this.cache = Array(this.numSets).fill(null).map(() => 
      Array(this.associativity).fill(null).map(() => ({
        valid: false,
        tag: 0,
        data: new Array(blockSize).fill(0),
        lastUsed: 0
      }))
    );
    
    this.hits = 0;
    this.misses = 0;
    this.accessCount = 0;
  }
  
  access(address) {
    this.accessCount++;
    const blockAddress = Math.floor(address / this.blockSize);
    const setIndex = blockAddress % this.numSets;
    const tag = Math.floor(blockAddress / this.numSets);
    
    // Check for hit
    for (let way = 0; way < this.associativity; way++) {
      const line = this.cache[setIndex][way];
      if (line.valid && line.tag === tag) {
        line.lastUsed = this.accessCount;
        this.hits++;
        return true; // Hit
      }
    }
    
    // Miss - need to replace
    this.misses++;
    const victimWay = this.findVictim(setIndex);
    this.cache[setIndex][victimWay] = {
      valid: true,
      tag: tag,
      data: new Array(this.blockSize).fill(address),
      lastUsed: this.accessCount
    };
    
    return false; // Miss
  }
  
  findVictim(setIndex) {
    // LRU replacement
    let lruWay = 0;
    let oldestTime = this.cache[setIndex][0].lastUsed;
    
    for (let way = 1; way < this.associativity; way++) {
      if (!this.cache[setIndex][way].valid) {
        return way; // Empty slot
      }
      if (this.cache[setIndex][way].lastUsed < oldestTime) {
        oldestTime = this.cache[setIndex][way].lastUsed;
        lruWay = way;
      }
    }
    
    return lruWay;
  }
  
  getStats() {
    const total = this.hits + this.misses;
    return {
      hits: this.hits,
      misses: this.misses,
      hitRate: total > 0 ? (this.hits / total * 100).toFixed(2) + '%' : '0%',
      missRate: total > 0 ? (this.misses / total * 100).toFixed(2) + '%' : '0%'
    };
  }
}
\`\`\``
        },
        {
          type: 'interactive_exercise',
          title: 'Cache Performance Analyzer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Design a cache performance analyzer that:

1. Simulates different cache configurations (direct-mapped, set-associative)
2. Analyzes memory access patterns and their cache behavior
3. Compares performance of different replacement policies
4. Calculates hit rates, miss rates, and average access times

Implement:
- Cache class with configurable parameters
- Memory access pattern generators
- Performance analysis methods
- Optimization recommendations

Test with a sequence: [0, 4, 8, 12, 16, 20, 0, 4] on a 4-block direct-mapped cache`,
            initialCode: `class CacheAnalyzer {
  constructor() {
    this.configurations = [];
  }
  
  // Create cache configuration
  createCache(type, size, blockSize, associativity = 1) {
    // Return cache object with specified configuration
    // Support: 'direct', 'set-associative', 'fully-associative'
  }
  
  // Simulate memory access pattern
  simulateAccesses(cache, addresses) {
    // Execute sequence of memory accesses
    // Track hits, misses, and replacement events
    // Return: { hits: number, misses: number, accesses: array }
  }
  
  // Analyze access pattern characteristics
  analyzePattern(addresses) {
    // Calculate spatial and temporal locality metrics
    // Return: { spatialLocality: number, temporalLocality: number }
  }
  
  // Compare cache configurations
  compareConfigurations(addresses, configs) {
    // Test multiple cache configurations with same access pattern
    // Return performance comparison
  }
  
  // Generate access patterns for testing
  generatePattern(type, length = 100) {
    // Generate different types of access patterns:
    // 'sequential', 'random', 'loop', 'strided'
  }
}

// Memory access patterns
const AccessPatterns = {
  sequential: (start, count) => {
    return Array.from({length: count}, (_, i) => start + i);
  },
  
  strided: (start, stride, count) => {
    return Array.from({length: count}, (_, i) => start + i * stride);
  },
  
  loop: (addresses, iterations) => {
    return Array(iterations).fill(addresses).flat();
  }
};

// Test implementation
const analyzer = new CacheAnalyzer();

// Test sequence: [0, 4, 8, 12, 16, 20, 0, 4]
const testSequence = [0, 4, 8, 12, 16, 20, 0, 4];

// Create direct-mapped cache: 4 blocks, block size 4
const cache = analyzer.createCache('direct', 16, 4, 1);
const results = analyzer.simulateAccesses(cache, testSequence);

console.log("Access sequence:", testSequence);
console.log("Cache results:", results);
console.log("Hit rate:", (results.hits / testSequence.length * 100).toFixed(1) + "%");`,
            expectedOutput: `Access sequence: [0, 4, 8, 12, 16, 20, 0, 4]
Cache results: { hits: 2, misses: 6, accesses: [...] }
Hit rate: 25.0%`,
            hints: [
              'Direct-mapped: block_index = (address / block_size) % num_blocks',
              'Set-associative: set_index = block_index % num_sets',
              'Track which addresses map to same cache locations',
              'Address 0 and 16 both map to block 0 in 4-block cache',
              'Hits occur when accessing previously loaded and still present data',
              'LRU replacement: replace least recently used block in set'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Memory System Design Project',
          title: 'Cache Hierarchy Optimization Analysis',
          points: 150,
          dueWeek: 11
        }
      ]
    },
    {
      id: 'system-performance',
      title: 'System Performance and Optimization',
      duration: '4 weeks',
      learningObjectives: [
        'Measure and analyze system performance metrics',
        'Identify performance bottlenecks in computer systems',
        'Apply optimization techniques for system efficiency',
        'Design high-performance computing solutions'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Performance Metrics and Optimization',
          content: `# System Performance Analysis

## Performance Metrics

### CPU Performance
- **Clock Speed**: Instructions per second capability
- **IPC (Instructions Per Cycle)**: Efficiency measure
- **CPI (Cycles Per Instruction)**: Inverse of IPC
- **Throughput**: Tasks completed per unit time
- **Latency**: Time to complete single task

### Memory Performance
- **Bandwidth**: Data transfer rate
- **Latency**: Access time delay
- **Cache Hit Rate**: Percentage of cache hits
- **Memory Hierarchy Efficiency**: Overall access time

### System Performance
- **MIPS (Million Instructions Per Second)**
- **FLOPS (Floating Point Operations Per Second)**
- **Benchmark Scores**: Standardized performance tests

## Amdahl's Law

**Formula**: Speedup = 1 / [(1-P) + P/S]

Where:
- P = Fraction of program that can be parallelized
- S = Speedup of parallelized portion

**Key Insight**: Overall speedup limited by sequential portion

## Performance Optimization Techniques

### Code-Level Optimizations
- **Loop Unrolling**: Reduce loop overhead
- **Function Inlining**: Eliminate call overhead
- **Data Structure Optimization**: Improve cache locality
- **Algorithm Selection**: Choose efficient algorithms

### System-Level Optimizations
- **Parallel Processing**: Utilize multiple cores
- **Pipeline Optimization**: Improve instruction flow
- **Cache Optimization**: Minimize cache misses
- **Memory Management**: Efficient allocation/deallocation

## JavaScript Performance Analyzer

\`\`\`javascript
class PerformanceAnalyzer {
  constructor() {
    this.benchmarks = new Map();
    this.systemInfo = this.getSystemInfo();
  }
  
  // Benchmark execution time
  benchmark(name, func, iterations = 1000) {
    const start = performance.now();
    
    for (let i = 0; i < iterations; i++) {
      func();
    }
    
    const end = performance.now();
    const totalTime = end - start;
    const avgTime = totalTime / iterations;
    
    this.benchmarks.set(name, {
      totalTime,
      avgTime,
      iterations,
      throughput: iterations / (totalTime / 1000) // ops/sec
    });
    
    return {
      name,
      avgTime: avgTime.toFixed(4) + ' ms',
      throughput: Math.round(iterations / (totalTime / 1000)) + ' ops/sec'
    };
  }
  
  // Memory usage analysis
  analyzeMemoryUsage(func) {
    const initialMemory = this.getMemoryUsage();
    func();
    const finalMemory = this.getMemoryUsage();
    
    return {
      initial: initialMemory,
      final: finalMemory,
      used: finalMemory - initialMemory
    };
  }
  
  // Cache locality test
  testCacheLocality() {
    const size = 1024 * 1024; // 1MB array
    const array = new Array(size).fill(0);
    
    // Sequential access (good locality)
    const sequentialTime = this.benchmark('sequential-access', () => {
      for (let i = 0; i < size; i++) {
        array[i] = i;
      }
    }, 10);
    
    // Random access (poor locality)
    const indices = Array.from({length: size}, (_, i) => i);
    this.shuffle(indices);
    
    const randomTime = this.benchmark('random-access', () => {
      for (let i = 0; i < size; i++) {
        array[indices[i]] = i;
      }
    }, 10);
    
    return {
      sequential: sequentialTime,
      random: randomTime,
      localityRatio: parseFloat(randomTime.avgTime) / parseFloat(sequentialTime.avgTime)
    };
  }
  
  // Calculate theoretical speedup using Amdahl's law
  amdahlSpeedup(parallelFraction, cores) {
    return 1 / ((1 - parallelFraction) + parallelFraction / cores);
  }
  
  // System information gathering
  getSystemInfo() {
    return {
      cores: navigator.hardwareConcurrency || 'unknown',
      memory: navigator.deviceMemory || 'unknown',
      platform: navigator.platform,
      userAgent: navigator.userAgent
    };
  }
  
  getMemoryUsage() {
    if ('memory' in performance) {
      return performance.memory.usedJSHeapSize;
    }
    return 0;
  }
  
  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }
  
  // Generate performance report
  generateReport() {
    return {
      systemInfo: this.systemInfo,
      benchmarks: Object.fromEntries(this.benchmarks),
      cacheTest: this.testCacheLocality(),
      recommendations: this.getOptimizationRecommendations()
    };
  }
  
  getOptimizationRecommendations() {
    const recommendations = [];
    
    if (this.systemInfo.cores > 1) {
      recommendations.push('Consider parallel processing for CPU-intensive tasks');
    }
    
    recommendations.push('Optimize data structures for cache locality');
    recommendations.push('Use efficient algorithms and data structures');
    recommendations.push('Profile code to identify bottlenecks');
    
    return recommendations;
  }
}
\`\`\``
        },
        {
          type: 'interactive_exercise',
          title: 'System Performance Optimizer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a comprehensive system performance analysis tool:

1. Create benchmarking functions for different algorithms
2. Implement Amdahl's Law calculator for parallel speedup
3. Analyze cache performance with different access patterns
4. Generate optimization recommendations

Your tool should:
- Compare sorting algorithms (bubble sort vs quicksort)
- Calculate theoretical speedup for parallel tasks
- Test memory access patterns
- Provide performance improvement suggestions

Test with: bubble sort vs quicksort on 1000 elements, calculate speedup for 90% parallel task on 4 cores`,
            initialCode: `class SystemOptimizer {
  constructor() {
    this.results = {};
  }
  
  // Benchmark sorting algorithms
  benchmarkSorting(algorithm, array) {
    const startTime = performance.now();
    const sorted = algorithm([...array]); // Copy to avoid mutation
    const endTime = performance.now();
    
    return {
      algorithm: algorithm.name,
      time: endTime - startTime,
      result: sorted
    };
  }
  
  // Amdahl's Law calculator
  calculateSpeedup(serialFraction, parallelFraction, processors) {
    // Speedup = 1 / (serialFraction + parallelFraction/processors)
    // Return: { theoretical: number, efficiency: number }
  }
  
  // Memory access pattern analyzer
  analyzeMemoryPattern(pattern, arraySize = 10000) {
    // Test different access patterns: sequential, random, strided
    // Measure time differences to infer cache behavior
    // Return: { pattern: string, time: number, efficiency: number }
  }
  
  // Generate optimization recommendations
  recommendOptimizations(benchmarkResults) {
    // Analyze results and suggest improvements
    // Consider: algorithm choice, parallelization, memory patterns
    // Return: array of recommendation strings
  }
  
  // Compare multiple implementations
  compareImplementations(implementations, testData) {
    // Run multiple versions and compare performance
    // Return performance comparison table
  }
}

// Sorting algorithm implementations
const SortingAlgorithms = {
  bubbleSort: function bubbleSort(arr) {
    const n = arr.length;
    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < n - i - 1; j++) {
        if (arr[j] > arr[j + 1]) {
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
        }
      }
    }
    return arr;
  },
  
  quickSort: function quickSort(arr) {
    if (arr.length <= 1) return arr;
    
    const pivot = arr[Math.floor(arr.length / 2)];
    const left = arr.filter(x => x < pivot);
    const middle = arr.filter(x => x === pivot);
    const right = arr.filter(x => x > pivot);
    
    return [...quickSort(left), ...middle, ...quickSort(right)];
  }
};

// Test implementation
const optimizer = new SystemOptimizer();

// Generate test data
const testArray = Array.from({length: 1000}, () => Math.floor(Math.random() * 1000));

// Benchmark sorting algorithms
const bubbleResult = optimizer.benchmarkSorting(SortingAlgorithms.bubbleSort, testArray);
const quickResult = optimizer.benchmarkSorting(SortingAlgorithms.quickSort, testArray);

console.log("Bubble Sort:", bubbleResult.time.toFixed(2) + "ms");
console.log("Quick Sort:", quickResult.time.toFixed(2) + "ms");
console.log("Performance ratio:", (bubbleResult.time / quickResult.time).toFixed(1) + "x");

// Calculate Amdahl's Law speedup
const speedup = optimizer.calculateSpeedup(0.1, 0.9, 4);
console.log("Theoretical speedup (90% parallel, 4 cores):", speedup.theoretical.toFixed(2) + "x");`,
            expectedOutput: `Bubble Sort: 45.23ms
Quick Sort: 2.14ms
Performance ratio: 21.1x
Theoretical speedup (90% parallel, 4 cores): 3.08x`,
            hints: [
              'Use performance.now() for accurate timing measurements',
              'Amdahl\'s Law: Speedup = 1 / (serialFraction + parallelFraction/processors)',
              'For 90% parallel: serialFraction = 0.1, parallelFraction = 0.9',
              'Bubble sort is O(n²), quicksort average case is O(n log n)',
              'Copy arrays before sorting to avoid mutating original data',
              'Calculate efficiency as speedup/processors for parallel analysis'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Performance Optimization Project',
          title: 'System Bottleneck Analysis and Optimization',
          points: 125,
          dueWeek: 15
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      duration: '120 minutes',
      points: 200,
      description: 'Comprehensive exam covering computer organization, processor architecture, and assembly language programming.'
    },
    {
      id: 'final',
      title: 'Final Examination',
      type: 'exam',
      date: 'Week 15',
      duration: '150 minutes',
      points: 250,
      description: 'Cumulative exam covering all course material including memory systems, cache design, and performance optimization.'
    },
    {
      id: 'system-design-project',
      title: 'Computer System Design Project',
      type: 'project',
      date: 'Week 15',
      points: 200,
      description: 'Design and implement a complete computer system simulator demonstrating understanding of architecture principles.'
    }
  ],
  resources: [
    {
      category: 'Textbooks',
      items: [
        'Computer Organization and Design by Patterson & Hennessy',
        'Computer Systems: A Programmer\'s Perspective by Bryant & O\'Hallaron',
        'Structured Computer Organization by Andrew Tanenbaum',
        'Computer Architecture: A Quantitative Approach by Hennessy & Patterson'
      ]
    },
    {
      category: 'Development Tools',
      items: [
        'MIPS Assembly Simulator (MARS)',
        'Intel x86 Assembly Development Environment',
        'ARM Development Studio',
        'Logic Design Software (Logisim)',
        'Performance Profiling Tools'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'MIT 6.004 Computation Structures',
        'Computer Architecture Course Materials',
        'Assembly Language Programming Tutorials',
        'CPU Design and Optimization Guides',
        'Memory Hierarchy Design Principles'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Module Assessments': '30%',
    'Midterm Exam': '25%',
    'Final Exam': '30%',
    'Design Project': '15%'
  },
  attendancePolicy: 'Regular attendance essential for hands-on system programming and architecture labs. Active participation required in assembly programming sessions.',
  latePolicy: 'System design assignments penalized 15% per day late due to complexity of integration work.',
  accommodations: 'Accommodations available including extended time for programming assignments and alternative formats for architectural diagrams.',
  academicIntegrity: 'Original system designs required. Code libraries must be properly attributed. Collaboration on concepts encouraged, individual implementation required.',
  technologyRequirements: 'Computer capable of running assembly development environments, simulation software, and performance analysis tools.',
  softwareRequirements: [
    'Assembly language development environment',
    'Logic design simulation software',
    'Performance monitoring tools',
    'System programming compilers',
    'Hardware simulation platforms'
  ],
  courseEvaluation: 'Continuous feedback through practical labs, design reviews, and comprehensive system architecture projects.'
};